import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDateTime, formatNumber, formatRelativeDate } from '@/shared/lib/format';

import type { ComplaintDetails } from '../types/complaint.types';

/**
 * The facts of the complaint, in one place beside the conversation.
 * `showOwner` for staff who cannot reassign it (they get no Owner card). `limited` for the
 * read-only view (complaints.read), which the backend sends without the customer or the order.
 */
export function ComplaintDetailsCard({
  data,
  showOwner = false,
  limited = false,
  zone,
}: {
  data: ComplaintDetails;
  showOwner?: boolean;
  limited?: boolean;
  /** The complaint's market zone. */
  zone: string;
}) {
  const complaint = data.complaint;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Details</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="flex flex-col gap-3 text-xs">
          {!limited && (
            <>
              <Field label="Customer" value={complaint.authorName ?? '—'} />
              {showOwner && <Field label="Owner" value={complaint.assigneeName ?? 'Unassigned'} />}
              <Field
                label="Order"
                value={
                  complaint.orderId ? (
                    <Link
                      to={`/orders/${complaint.orderId}`}
                      className="text-primary inline-flex items-center gap-1 font-medium hover:underline"
                    >
                      #{complaint.orderId}
                      <ExternalLink className="size-3" aria-hidden />
                      <span className="sr-only">Open the order</span>
                    </Link>
                  ) : (
                    'Not linked to an order'
                  )
                }
              />
            </>
          )}
          <Field label="Type" value={complaint.complaintType ?? '—'} />
          <Field label="Raised" value={formatDateTime(complaint.complaintDate, zone)} />
          <Field
            label="Last message"
            value={
              complaint.lastMessageAt ? (
                <span title={formatDateTime(complaint.lastMessageAt, zone)}>
                  {formatRelativeDate(complaint.lastMessageAt)}
                </span>
              ) : (
                '—'
              )
            }
          />
          <Field label="Messages" value={formatNumber(complaint.messageCount)} />
        </dl>
      </CardContent>
    </Card>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-muted-foreground shrink-0">{label}</dt>
      <dd className="min-w-0 truncate text-end font-medium">{value}</dd>
    </div>
  );
}
